import React, { useEffect, useRef, useState } from 'react';

function LazyImage({
  src,
  alt = '',
  className,
  style = {},
  onClick,
  placeholderStyle = {},
  wrapperStyle = {},
  wrapperClassName,
  rootMargin = '200px',
  eager = false,
  loading: loadingProp,
  ...rest
}) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(eager);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(false);
  }, [src]);

  useEffect(() => {
    if (eager) {
      setIsVisible(true);
      return undefined;
    }
    const el = ref.current;
    if (!el) return;

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              setIsVisible(true);
              obs.unobserve(entry.target);
            }
          });
        },
        { root: null, rootMargin, threshold: 0.01 }
      );

      observer.observe(el);
      return () => observer.disconnect();
    }

    // Fallback: if no IntersectionObserver, load immediately
    setIsVisible(true);
  }, [eager, rootMargin]);

  const {
    width,
    height,
    minHeight,
    maxHeight,
    minWidth,
    maxWidth,
    ...restStyle
  } = style || {};

  const effectiveWidth = width !== undefined ? width : '100%';
  const imageStyle = {
    display: 'block',
    width: effectiveWidth,
    height: height || 'auto',
    maxHeight,
    minWidth,
    maxWidth,
    objectFit: 'contain',  // Fixes aspect ratio in image preview Modal
    ...restStyle,
  };

  const placeholderMinHeight =
    minHeight ?? height ?? maxHeight ?? placeholderStyle.minHeight ?? 150;

  const placeholderStyles = {
    display: 'block',
    width: effectiveWidth,
    height,
    maxHeight,
    minWidth,
    maxWidth,
    backgroundColor: '#f0f0f0',
    minHeight: placeholderMinHeight,
    ...placeholderStyle,
  };

  const wrapperCombinedStyle = {
    position: 'relative',
    width: effectiveWidth,
    minWidth,
    maxWidth,
    display: 'flex',  // Same size in step gallery
    justifyContent: 'center',  // Centered in main step image view
    // alignItems: 'center', // Counteracts "display: flex" effect
    ...wrapperStyle,
  };

  const handleImageLoad = () => {
    setIsLoaded(true);
    //console.log('Image loaded:', src);
  };

  const handleImageError = () => {
    // setIsLoaded(true); // Consider error as "loaded" to stop showing placeholder
    console.log('Error loading image:', src);
  };

  return (
    <div ref={ref} className={wrapperClassName} style={wrapperCombinedStyle}>
      {isVisible ? (
        <>
          {/* Display placeholder while image is loading */}
          {/* {!isLoaded && (
            <div className={className} style={placeholderStyles} />
          )} */}
          {/* Display image when loaded */}
          <img
            src={src}
            alt={alt}
            width={isLoaded ? width : effectiveWidth}
            height={isLoaded ? height : placeholderMinHeight}
            className={isLoaded ? `${className}` : `${className} placeholder-img`}
            style={imageStyle}
            onClick={onClick}
            loading={loadingProp ?? (eager ? 'eager' : 'lazy')}
            // Hide image while loading, and clear the hidden flag when loaded.
            // {...(!isLoaded && { hidden: true })}
            onLoad={handleImageLoad}
            onError={handleImageError}
            {...rest}
          />
        </>
      ) : (
        <div className={className} style={placeholderStyles} />
      )}
    </div>
  );
}

export default LazyImage;
